import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import Select from "@/components/atoms/Select";
import { EMAIL_REGEX } from "@/constants/regexs";
import {
  INVALID_EMAIL2_MESSAGE,
  INVALID_FIRSTNAME_MESSAGE,
  INVALID_FORM_MESSAGE,
  INVALID_LASTNAME_MESSAGE,
  INVALID_MAXIMUM_CARACTERE_MESSAGE,
  INVALID_MINIMUM_CARACTERE_MESSAGE,
  INVALID_PHONE2_MESSAGE,
} from "@/constants/validatorMessages";
import getDifferenceBetweenTwoObjects from "@/helpers/FormHelpers";
import { CardEditProps } from "@/interfaces/Card";
import Person, { GENDER_TYPES, PersonForm } from "@/interfaces/Person";
import { useGetPersonQuery, useUpdatePersonMutation } from "@/services/people";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { FC, useEffect } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";
import CardHeader from "../../card-headers/CardHeader";

const PersonEditCard: FC<CardEditProps<Person>> = ({ id, callBack }) => {
  const { data, isLoading, isError, error } = useGetPersonQuery(id);
  const [updatePersonAsync] = useUpdatePersonMutation();
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<Partial<PersonForm>>();

  useEffect(() => {
    if (data) {
      const { id, company, gender, ...rest } = data;
      callBack?.(data);
      reset({
        ...rest,
        gender: GENDER_TYPES.find((element) => element.value === gender),
      });
    }
  }, [data]);

  const onSubmit: SubmitHandler<Partial<PersonForm>> = (newData) => {
    const result = getDifferenceBetweenTwoObjects<Partial<PersonForm>>(
      data,
      newData,
    );
    const phone = result.phone && parsePhoneNumber(result.phone, "FR").number;
    toast.promise(
      updatePersonAsync([
        id,
        {
          ...result,
          phone,
          gender: result.gender?.value,
        },
        undefined,
      ]).unwrap(),
      {
        pending: "Modification de la personne en cours ...",
        success: {
          render() {
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                La personne à bien été modifiée 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            }

            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<Partial<PersonForm>> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit, onError)}>
      <Card>
        <CardHeader title="Modification de l'employé" />
        <Card.Body className="bg-light">
          <Col lg={6}>
            <Form.Group>
              <Form.Label>
                Genre :
                <Controller
                  control={control}
                  name="gender"
                  render={({ field }) => (
                    <Select
                      options={GENDER_TYPES}
                      getOptionValue={(option) => option.value}
                      getOptionLabel={(option) => option.label}
                      onChange={field.onChange}
                      value={field.value}
                    />
                  )}
                />
              </Form.Label>
            </Form.Group>
          </Col>
          <Row className="gx-3">
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Prénom <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  placeholder="ex : Remi"
                  type="text"
                  {...register("firstname", {
                    required: INVALID_FIRSTNAME_MESSAGE,
                    minLength: {
                      value: 2,
                      message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                    },
                    maxLength: {
                      value: 255,
                      message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.firstname}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstname?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Nom de famille <span className="text-danger">*</span> :
                </Form.Label>
                <Form.Control
                  placeholder="ex : Dupont"
                  type="text"
                  {...register("lastname", {
                    required: INVALID_LASTNAME_MESSAGE,
                    minLength: {
                      value: 2,
                      message: INVALID_MINIMUM_CARACTERE_MESSAGE,
                    },
                    maxLength: {
                      value: 255,
                      message: INVALID_MAXIMUM_CARACTERE_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.lastname}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastname?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email :</Form.Label>
                <Form.Control
                  placeholder="ex : remi@groupe-pi.com"
                  type="email"
                  {...register("email", {
                    pattern: {
                      value: EMAIL_REGEX,
                      message: INVALID_EMAIL2_MESSAGE,
                    },
                  })}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numéro de téléphone :</Form.Label>
                <Form.Control
                  placeholder="ex : +33 6 34 83 55 32"
                  type="text"
                  {...register("phone", {
                    validate: (value) => {
                      if (value && !isValidPhoneNumber(value, "FR")) {
                        return INVALID_PHONE2_MESSAGE;
                      }
                    },
                  })}
                  isInvalid={!!errors.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Date d'anniversaire :</Form.Label>
                <Form.Control
                  type="date"
                  {...register("birthdate")}
                  isInvalid={!!errors.birthdate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.birthdate?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Ville de naissance :</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="ex : Paris 17"
                  {...register("cityOfBirth")}
                  isInvalid={!!errors.cityOfBirth}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.cityOfBirth?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Adresse</Form.Label>
                <Form.Control
                  placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                  type="string"
                  {...register("address")}
                  isInvalid={!!errors.address}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label>Ville :</Form.Label>
                <Form.Control
                  placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                  type="string"
                  {...register("city")}
                  isInvalid={!!errors.city}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.city?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={3}>
              <Form.Group className="mb-3">
                <Form.Label>Code postale: </Form.Label>
                <Form.Control
                  placeholder="ex : 3 Rue de la Paix, 75002 Paris"
                  type="string"
                  {...register("zipCode")}
                  isInvalid={!!errors.zipCode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.zipCode?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Intitulé de poste:</Form.Label>
                <Form.Control
                  placeholder="ex : Développeur"
                  type="text"
                  {...register("jobTitle", {
                    minLength: {
                      value: 2,
                      message: "Ce champ doit avoir au minimum 2 caractères",
                    },
                    maxLength: {
                      value: 255,
                      message: "Ce champ doit avoir au minimum 255 caractères",
                    },
                  })}
                  isInvalid={!!errors.jobTitle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.jobTitle?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Type de contrat :</Form.Label>
                <Form.Control
                  placeholder="ex : CDI, CDDE, CDD, etc..."
                  type="text"
                  {...register("contractType")}
                  isInvalid={!!errors.contractType}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.contractType?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3">
                <Form.Label>Numéro de sécurité sociale</Form.Label>
                <Form.Control
                  placeholder="ex : 1 60 04 25 311 114"
                  type="number"
                  {...register("socialSecurityNumber")}
                  isInvalid={!!errors.socialSecurityNumber}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.socialSecurityNumber?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Button
            type="submit"
            variant="success"
            disabled={isLoading}
            className="mt-3"
          >
            Confirmer
          </Button>
        </Card.Body>
      </Card>
    </Form>
  );
};

export default PersonEditCard;
